import { Injectable } from '@angular/core';
import { API } from '@app-core/constants/api.constants';
import { FleetStatsParams, setParams } from '@app-core/models/core.model';
import { HttpCacheService } from '@app-core/services/cache/cache.service';
import { CreateScheduleBody, DownloadFleetSafetyReportsParams, ScheduleConfig } from '@app-reports/models/reports.model';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import {
  AsyncReportsParams,
  AsyncReportsResponse,
  GetScheduledReportsParams,
  GetScheduledReportsResponse,
  PatchScheduleBody,
  ScheduledReportRow,
  Timezone,
} from '@app-reports/models/reports.model';
import { DataService } from '@app-core/services/data/data.service';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import * as momentTz from 'moment-timezone';
import { COMBINED_SCHEDULED_REPORTS, FREQUENCY, ReportType } from '@app-reports/constants/reports.constants';
import { MatDialog } from '@angular/material/dialog';
import { SsoDialogComponent } from '@app-shared/components/dialog/sso-dialog/sso-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  public tagIds = [];
  private _scheduleDetails: ScheduledReportRow;

  constructor(
    private http: HttpClient,
    private cacheService: HttpCacheService,
    private dataService: DataService,
    private durationService: DurationRangeService,
    private dialog: MatDialog
  ) {}

  public get scheduleDetails() {
    return this._scheduleDetails;
  }

  public set scheduleDetails(schedule: ScheduledReportRow) {
    this._scheduleDetails = schedule;
  }

  public getArchivedReports(params: any, isRefresh?: boolean): Observable<any> {
    const httpOptions = {
      params: setParams(params),
    };
    if (isRefresh) {
      this.cacheService.burstCache$.next(API.GET_ARCHIVED_REPORTS);
    }
    return this.http.get(API.GET_ARCHIVED_REPORTS, httpOptions);
  }

  public getArchivedReportsAggregate(params: any, isRefresh?: boolean): Observable<any> {
    const httpOptions = {
      params: setParams(params),
    };
    if (isRefresh) {
      this.cacheService.burstCache$.next(API.GET_ARCHIVED_REPORTS_AGGREGATE);
    }
    return this.http.get(API.GET_ARCHIVED_REPORTS_AGGREGATE, httpOptions);
  }

  public getFleetStats(params: FleetStatsParams): Observable<any> {
    if (this.tagIds && this.tagIds.length) {
      params['tagIds[]'] = this.tagIds;
    }
    const httpOptions = {
      params: setParams(params),
    };
    return this.http.get(API.GET_FLEET_STATS, httpOptions);
  }

  public getFleetDeviceStats(params: any): Observable<any> {
    if (this.tagIds && this.tagIds.length) {
      params['tagIds[]'] = this.tagIds;
    }
    const httpOptions = {
      params: setParams(params),
    };
    return this.http.get(API.GET_FLEET_DEVICE_STATS, httpOptions);
  }

  public getTopDriversList(params: any): Observable<any> {
    if (this.tagIds && this.tagIds.length) {
      params['tagIds[]'] = this.tagIds;
    }
    const httpOptions = {
      params: setParams(params),
    };
    return this.http.get(API.GET_DRIVER_LIST_V2, httpOptions).pipe(
      map((res: any) => {
        const topDriversList = res.data.drivers || [];
        return {
          topDrivers: topDriversList,
          skip: res?.skip,
          limit: res?.limit,
          totalCount: res?.totalCount,
        };
      })
    );
  }

  public getCoachRecommendList(params: any): Observable<any> {
    const api = API.COACHING_RECPMMENDATIONS;
    const httpOptions = {
      params: setParams(params),
    };
    return this.http.get(api, httpOptions).pipe(
      map((res: any) => {
        const coachableDriversList = res.rows;
        return {
          coachableDrivers: coachableDriversList,
        };
      })
    );
  }

  public getImprovedDriverList(params: any): Observable<any> {
    if (this.tagIds && this.tagIds.length) {
      params['tagIds[]'] = this.tagIds;
    }
    const httpOptions = {
      params: setParams(params),
    };
    return this.http.get(API.GET_IMPROVED_DRIVERS, httpOptions);
  }

  public getFleetEventTrend(params: any): Observable<any> {
    if (this.tagIds && this.tagIds.length) {
      params['tagIds[]'] = this.tagIds;
    }
    const httpOptions = {
      params: setParams(params),
    };
    return this.http.get(API.GET_FLEET_EVENT_TREND, httpOptions);
  }

  public downloadFleetSafetyReport(params: DownloadFleetSafetyReportsParams): Observable<any> {
    if (this.tagIds && this.tagIds.length) {
      params['tagIds[]'] = this.tagIds;
    }
    const httpOptions = {
      params: setParams(params),
    };
    return this.http.get(API.GET_FLEET_SAFETY_REPORT, { ...httpOptions, observe: 'response', responseType: 'blob' });
  }

  public downloadCoachingSessionReport(params: any, sessionId: number) {
    const httpOptions = {
      params: setParams(params),
    };
    const url = API.GET_FCOACHING_SESSION_REPORT(sessionId);
    return this.http.get(url, { ...httpOptions, observe: 'response', responseType: 'blob' });
  }

  public getCoachReccomendList(params: any): Observable<any> {
    if (this.tagIds && this.tagIds.length) {
      params['tagIds[]'] = this.tagIds;
    }
    const api = API.COACHING_RECPMMENDATIONS;
    const httpOptions = {
      params: setParams(params),
    };
    return this.http.get(api, httpOptions);
  }

  public extractFilename(contentDispositionHeader: string): string {
    return contentDispositionHeader.split(';')[1].trim().split('=')[1].replace(/"/g, '');
  }

  public downloadFile(data: any, filename = 'data', type: string) {
    const blob = new Blob([data], { type });
    const downloadLink = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const isSafariBrowser = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;
    if (isSafariBrowser) {
      // if Safari open in new window to save file with random filename.
      downloadLink.setAttribute('target', '_blank');
    }
    downloadLink.setAttribute('href', url);
    downloadLink.setAttribute('download', filename);
    downloadLink.style.visibility = 'hidden';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  public getCoachingSessionDetails(coachingSessionId: number, params) {
    const httpOptions = {
      params: setParams(params),
    };
    const url = API.COACHING_SESSION_DETAILS(coachingSessionId);
    return this.http.get(url, httpOptions);
  }

  public getCoachingEffectivenessDriverDetails(coachingSessionId: number, driverId: string, params) {
    const httpOptions = {
      params: setParams(params),
    };
    const url = API.GET_COACHING_EFFECTIVEESS_DRIVER_REPORT(coachingSessionId, driverId);
    return this.http.get(url, httpOptions);
  }

  public getCoachingSessionList(params) {
    const httpOptions = {
      params: setParams(params),
    };
    const url = API.LIST_COACHING_SESSION;
    return this.http.get(url, httpOptions);
  }

  public getDriverTripsAggregate(driverId: string, params) {
    const httpOptions = {
      params: setParams(params),
    };
    const url = API.GET_DRIVER_TRIPS_AGGREGATE(driverId);
    return this.http.get(url, httpOptions);
  }

  public getAsyncReportsList(params: AsyncReportsParams): Observable<AsyncReportsResponse> {
    const httpOptions = {
      params: setParams(params),
    };
    return this.http.get<AsyncReportsResponse>(API.ASYNC_REPORTS, httpOptions);
  }

  public downloadFiles(downloadUrl: string) {
    const downloadLink = document.createElement('a');
    const isSafariBrowser = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;
    if (isSafariBrowser) {
      // if Safari open in new window to save file with random filename.
      downloadLink.setAttribute('target', '_blank');
    }
    downloadLink.setAttribute('href', downloadUrl);
    downloadLink.style.visibility = 'hidden';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  public getScheduledReports(params: GetScheduledReportsParams, isRefresh: boolean = false): Observable<GetScheduledReportsResponse> {
    const httpOptions = {
      params: setParams(params),
    };
    if (isRefresh) {
      this.clearScheduledReportsCache();
    }
    return this.http.get<GetScheduledReportsResponse>(API.SCHEDULED_REPORTS, httpOptions);
  }

  public getScheduleReportDetails(scheduleId: string, isRefresh: boolean = false): Observable<ScheduledReportRow> {
    if (isRefresh) {
      this.clearScheduledReportsCache();
    }
    return this.http.get<ScheduledReportRow>(API.SCHEDULED_REPORT_ID(scheduleId));
  }

  public createScheduledReports(body: CreateScheduleBody) {
    if (body?.scheduleConfiguration) {
      body.scheduleConfiguration = { ...body.scheduleConfiguration, ...this.getTimezoneOffset() };
      body.scheduleConfiguration = this.filterValues(body.scheduleConfiguration);
    }
    return this.http.post(API.SCHEDULED_REPORTS, body).pipe(tap(() => this.clearScheduledReportsCache()));
  }

  public updateScheduledReport(scheduleId: string, body: PatchScheduleBody) {
    if (body?.scheduleConfiguration) {
      body.scheduleConfiguration = { ...body.scheduleConfiguration, ...this.getTimezoneOffset() };
    }

    return this.http.patch(API.SCHEDULED_REPORT_ID(scheduleId), body).pipe(tap(() => this.clearScheduledReportsCache()));
  }

  public deleteScheduledReport(scheduleId: string) {
    return this.http.delete(API.SCHEDULED_REPORT_ID(scheduleId)).pipe(tap(() => this.clearScheduledReportsCache()));
  }

  private clearScheduledReportsCache() {
    this.cacheService.burstCache$.next(API.SCHEDULED_REPORTS);
  }

  private getTimezoneOffset(): Timezone {
    const selectedTimezone = this.dataService._currentTimeZone.getValue();
    if (selectedTimezone === 'Local') {
      const localTimezone = momentTz.tz.guess(true);
      return { localTimezoneId: localTimezone };
    } else {
      return { timezoneId: selectedTimezone };
    }
  }

  public getUTCTime(time, sendDay, frequency) {
    const tz = this.currentTimezone;
    if (!tz) {
      return;
    }
    const format: string = 'HH:mm';
    const now = momentTz().tz(tz);
    const [hour, minute] = time.split(':').map(Number);
    now.hour(hour)?.minute(minute)?.second(0);
    let result = {
      sendTimeUTC: time,
      dayOfTheSchedule: sendDay,
    };
    if (frequency === FREQUENCY.WEEKLY.key) {
      const day = (sendDay + 7) % 7;
      now.day(day);
      const utc = now.clone().utc();
      result.dayOfTheSchedule = (utc.day() + 7) % 7;
      result.sendTimeUTC = utc.format(format);
    }
    if (frequency === FREQUENCY.MONTHLY.key) {
      now.month(7);
      now.date(sendDay);
      const utc = now.clone().utc();
      result.dayOfTheSchedule = utc.date();
      result.sendTimeUTC = utc.format(format);
    }
    if (frequency === FREQUENCY.DAILY.key) {
      const utc = now.clone().utc();
      result.sendTimeUTC = utc.format(format);
    }
    return result;
  }

  public getUserTime(time, sendDay, frequency) {
    const tz = this.currentTimezone;
    if (!tz) {
      return;
    }
    const format: string = 'HH:mm';
    const now = momentTz().utc();
    const [hour, minute] = time?.split(':').map(Number);
    now.hour(hour)?.minute(minute)?.second(0);
    let result = {
      sendTime: time,
      sendDay: sendDay,
    };
    if (frequency === FREQUENCY.WEEKLY.key) {
      const day = (sendDay + 7) % 7;
      now.day(day);
      const userTime = now.clone().tz(tz);
      result.sendDay = (userTime.day() + 7) % 7;
      result.sendTime = userTime.format(format);
    }
    if (frequency === FREQUENCY.MONTHLY.key) {
      now.month(7);
      now.date(sendDay);
      const userTime = now.clone().tz(tz);
      result.sendDay = userTime.date();
      result.sendTime = userTime.format(format);
    }
    if (frequency === FREQUENCY.DAILY.key) {
      const userTime = now.clone().tz(tz);
      result.sendTime = userTime.format(format);
    }
    return result;
  }

  get currentTimezone() {
    const timezone = this.getTimezoneOffset();
    return timezone?.timezoneId || timezone?.localTimezoneId;
  }

  public updateReportsDurationOptions() {
    const dataRetentionDays = this.durationService.dataRetentionDays;

    Object.values(COMBINED_SCHEDULED_REPORTS).forEach((reportType) => {
      const defaultDurationOptions = reportType.reportDuration;
      const updatedDurationOptions = defaultDurationOptions.filter((item) => {
        if (typeof item.key === 'number') {
          return item.key <= dataRetentionDays;
        }
        return false;
      });
      reportType.reportDuration = updatedDurationOptions;
    });
  }

  private filterValues(config: ScheduleConfig) {
    const filteredConfig = Object.keys(config)?.reduce((acc, key) => {
      if (key && config[key] !== null) {
        acc[key] = config[key];
      }
      return acc;
    }, {} as ScheduleConfig);
    return filteredConfig;
  }

  public getSubtypeKey(reportType, config) {
    let subtype = '';

    if (!config) {
      return '';
    }

    switch (reportType) {
      case 'FLEET_SAFETY':
        break;

      case 'COACHING_SESSION':
        break;

      case 'FLEET_HEALTH_EVENTS':
        subtype = config?.eventType;
        break;

      case 'FLEET_CAMERA_EVENTS':
        subtype = config?.eventType;
        break;

      case 'FLEET_DEVICE_PING':
        subtype = config?.daysInactive;
        break;
      case 'PRIVACY_MODE':
        break;
      case 'EVENT_LIST':
        break;
      case 'EVENT_COUNT':
        break;

      case 'TRIPS':
        break;
    }

    return subtype;
  }

  public getSubtypeParams(reportType, reportSubtype) {
    let params = {};

    switch (reportType) {
      case 'FLEET_SAFETY':
        break;

      case 'COACHING_SESSION':
        break;

      case 'TRIPS':
        break;

      case 'FLEET_HEALTH_EVENTS':
        if (reportSubtype === 'ALL') {
          params['eventType'] = null;
        } else {
          params['eventType'] = reportSubtype;
        }
        break;

      case 'FLEET_CAMERA_EVENTS':
        if (reportSubtype === 'ALL') {
          params['eventType'] = null;
        } else {
          params['eventType'] = reportSubtype;
        }
        break;

      case 'FLEET_DEVICE_PING':
        if (reportSubtype === 'ALL') {
          params['daysInactive'] = null;
        } else {
          params['daysInactive'] = reportSubtype;
        }
        break;
      case 'PRIVACY_MODE':
        break;

      case 'EVENT_LIST':
        break;

      case 'EVENT_COUNT':
        break;
    }

    return params;
  }

  public openSSOConsentDialog() {
    let time = new Date();
    const consentTimeStamp = time.toISOString();

    const dialogRef = this.dialog.open(SsoDialogComponent, {
      position: { top: '24px' },
      width: '360px',
      height: '225px',
      data: {
        consentTime: consentTimeStamp,
      },
    });
    return dialogRef;
  }

  public getAllowedReports(availableReports) {
    const supportAsyncAssetReport = this.durationService.supportAsyncAssetReport;

    const allowedReports = supportAsyncAssetReport
      ? availableReports
      : availableReports.filter((report: any) => {
          return report.key !== ReportType.fleetAssetReport.key;
        });

    return allowedReports;
  }

  public getReportTypes(reports) {
    const availableReports = this.getAllowedReports(reports);
    const featureFlags = this.durationService.planDetails?.featureFlags;

    if (!featureFlags || !featureFlags?.length) {
      return availableReports;
    }

    const allowedReports = availableReports.filter((report: any) => {
      if (report.hasOwnProperty('featureFlag')) {
        return featureFlags.includes(report.featureFlag);
      }
      return true;
    });

    return allowedReports;
  }
}
